/**
 * Realistische Restzeit für Mastering-Jobs.
 *
 * Die alte Formel (elapsed * (100-p) / p) explodiert, weil der Fortschritt
 * stufenweise springt (z. B. QC startet bei ~70 %) und während QC nur
 * langsam „fake“-tickt – die Restzeit wächst dann mit der Gesamt-Laufzeit.
 */

const STEP_ETA = {
  preflight: { remainingSec: 15, progressStart: 0, progressEnd: 12 },
  decode: { remainingSec: 20, progressStart: 5, progressEnd: 15 },
  analyze: { remainingSec: 50, progressStart: 12, progressEnd: 35 },
  analyzing: { remainingSec: 50, progressStart: 12, progressEnd: 35 },
  plan: { remainingSec: 35, progressStart: 28, progressEnd: 45 },
  mastering: { remainingSec: 40, progressStart: 28, progressEnd: 45 },
  ai_cleanup: { remainingSec: 75, progressStart: 35, progressEnd: 55 },
  render: { remainingSec: 80, progressStart: 45, progressEnd: 70 },
  rendering: { remainingSec: 80, progressStart: 45, progressEnd: 70 },
  limit_safety: { remainingSec: 40, progressStart: 65, progressEnd: 75 },
  // EBU R128 Messung: typisch unter 2 Min., lange Tracks bis ~3 Min.
  qc: { remainingSec: 90, progressStart: 70, progressEnd: 90 },
  tagging: { remainingSec: 20, progressStart: 90, progressEnd: 99 },
  finalizing: { remainingSec: 20, progressStart: 90, progressEnd: 99 },
}

/** Grobe Restzeit der Schritte nach dem aktuellen (Sekunden). */
const REMAINING_AFTER = {
  preflight: 280,
  decode: 260,
  analyze: 210,
  analyzing: 210,
  plan: 175,
  mastering: 175,
  ai_cleanup: 100,
  render: 110,
  rendering: 110,
  limit_safety: 100,
  qc: 25,
  tagging: 0,
  finalizing: 0,
}

const MAX_ETA_SEC = 180 // nie mehr als 3 Min. anzeigen (verhindert „immer mehr Min.“)

export function estimatedSecondsRemaining(job) {
  if (!job || (job.status !== 'pending' && job.status !== 'running')) return null
  const progress = Number(job.progress)
  if (!Number.isFinite(progress) || progress <= 0 || progress >= 100) return null

  const step = job.step
  const cfg = step ? STEP_ETA[step] : null

  if (cfg) {
    const span = Math.max(1, cfg.progressEnd - cfg.progressStart)
    const within = Math.min(1, Math.max(0, (progress - cfg.progressStart) / span))
    const thisStepLeft = cfg.remainingSec * (1 - within)
    // Solange der Schritt läuft und Fortschritt noch nicht am Ende: mind. etwas Restzeit
    const floor = within < 0.95 && progress < 95 ? 8 : 0
    const later = REMAINING_AFTER[step] ?? Math.max(0, Math.round((100 - progress) * 1.2))
    const eta = Math.max(floor, Math.round(thisStepLeft + later * Math.max(0, 1 - within * 0.3)))
    return Math.min(eta, MAX_ETA_SEC)
  }

  // Fallback: linear, aber hart gedeckelt
  const started = job.started_at ? new Date(job.started_at).getTime() : null
  if (!started || Number.isNaN(started)) {
    // Ohne Startzeit: grob aus Rest-Prozent
    return Math.min(MAX_ETA_SEC, Math.round((100 - progress) * 1.5))
  }
  const elapsedSec = Math.max(1, (Date.now() - started) / 1000)
  const linear = (elapsedSec * (100 - progress)) / progress
  return Math.min(MAX_ETA_SEC, Math.max(0, Math.round(linear)))
}

/** Format ohne führendes „ca.“ – Aufrufer schreibt z. B. „Noch ca. …“. */
export function formatRemaining(seconds) {
  if (seconds == null) return null
  if (seconds < 60) return `${Math.round(seconds)} Sek.`
  const m = Math.round(seconds / 60)
  return m <= 1 ? '1 Min.' : `${m} Min.`
}

/** Gesamtlaufzeit des Jobs in Sekunden (aus started_at), sonst null. */
export function jobElapsedSeconds(job) {
  if (!job?.started_at) return null
  const started = new Date(job.started_at).getTime()
  if (Number.isNaN(started)) return null
  return Math.max(0, Math.floor((Date.now() - started) / 1000))
}
